<template>
  <div v-if="!hasPermission('List TISS Batch')">Você não tem permissão para acessar esta página!</div>
  <div v-else>
    <Toolbar/>

    <div class="container-xxl">
      <div class="card mb-5">
        <div class="card-header border-0 pt-6">
          <div class="card-title">
            <h3 class="fw-bold m-0">Guias</h3>
          </div>
          <div class="card-toolbar">
            <router-link to="/tiss/batches" class="btn btn-light-info me-2">
              <i class="bi bi-folder"></i> Lotes
            </router-link>
            <!-- <button v-if="hasPermission('Configure TISS Guide')"
                    class="btn btn-light-primary me-2" 
                    @click="openSettings()">
              <i class="bi bi-gear"></i> Configurações
            </button> -->
            <button v-if="selectedGuides.length > 0 && isSelectableBatchMode" 
                    class="btn btn-success me-2" 
                    @click="openBatchModal()">
              <i class="bi bi-folder-check"></i> Criar Lote ({{ selectedGuides.length }})
            </button>
            <button class="btn btn-primary" @click="openModal()">
              <i class="bi bi-plus-lg"></i> Nova Guia
            </button>
          </div>
        </div>

        <div class="card-body pt-0">
          <!-- Filtros -->
          <div class="card mb-5 mb-xl-8">
            <div class="card-header border-0 pt-5">
              <h3 class="card-title align-items-start flex-column">
                <span class="card-label fw-bold fs-3 mb-1">Filtros</span>
              </h3>
            </div>
            <div class="card-body pt-0">
              <div class="row g-3">
                <div class="col-md-2">
                  <label class="form-label">Data Inicial</label>
                  <input v-model="filters.start_date" type="date" class="form-control">
                </div>
                <div class="col-md-2">
                  <label class="form-label">Data Final</label>
                  <input v-model="filters.end_date" type="date" class="form-control">
                </div>
                <div class="col-md-2">
                  <label class="form-label">Profissional</label>
                  <select v-model="filters.id_prof" class="form-select">
                    <option value="">Todos</option>
                    <option v-for="prof in professionals" :key="prof.id_prof" :value="prof.id_prof">
                      {{ prof.prof_name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label">Convênio</label>
                  <select v-model="filters.id_insurance" class="form-select">
                    <option value="">Todos</option>
                    <option v-for="insurance in insurances" :key="insurance.id_insurance" :value="insurance.id_insurance">
                      {{ insurance.insurance_name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label">Buscar</label>
                  <input v-model="filters.search" class="form-control" placeholder="Número da guia, paciente...">
                </div>
                <div class="col-md-2">
                  <label class="form-label">Tipo de Guia</label>
                  <select v-model="filters.guide_type" class="form-select">
                    <option value="">Todos</option>
                    <option value="CONSULTA">Consulta</option>
                    <option value="SP_SADT">SP/SADT</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label">Disponíveis para lote</label>
                  <select v-model="filters.batch_type_available" class="form-select" @change="onBatchAvailabilityFilterChange">
                    <option value="">Todos</option>
                    <option value="tiss">Lote de recebimento (TISS)</option>
                    <option value="payment">Lote de pagamento</option>
                  </select>
                </div>
              </div>
              <div class="row g-3 mt-2">
                <div class="col-md-2">
                  <label class="form-label">Status</label>
                  <select v-model="filters.status" class="form-select">
                    <option value="">Todos</option>
                    <option value="generated">Gerada</option>
                    <option value="sent">Enviada</option>
                    <option value="processed">Processada</option>
                    <option value="rejected">Glosada</option>
                    <option value="cancelled">Cancelada</option>
                  </select>
                </div>
                <div class="col-md-2 d-flex align-items-end">
                  <button @click="fetchGuides" class="btn btn-primary w-100">
                    <i class="bi bi-search"></i> Filtrar
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Tabela -->
          <div class="table-responsive">
            <table class="table table-row-bordered table-row-gray-300 align-middle gs-0 gy-4">
              <thead>
                <tr class="fw-bold text-muted">
                  <th class="min-w-50px">
                    <input type="checkbox" 
                           :checked="allSelected" 
                           @change="toggleSelectAll"
                           class="form-check-input">
                  </th>
                  <th class="min-w-100px">Número</th>
                  <th class="min-w-150px">Paciente</th>
                  <th class="min-w-100px">Tipo</th>
                  <th v-if="!filters.batch_type_available" class="min-w-200px">Status</th>
                  <th v-if="filters.batch_type_available" class="min-w-120px">Profissional</th>
                  <th v-if="filters.batch_type_available" class="min-w-100px">Operadora</th>
                  <th v-if="filters.batch_type_available" class="min-w-90px">Valor</th>
                  <th class="min-w-100px">Data</th>
                  <th class="min-w-120px text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="guide in guides.data" :key="guideId(guide)">
                  <td>
                    <input type="checkbox" 
                           :value="guideId(guide)"
                           v-model="selectedGuides"
                           class="form-check-input"
                           :disabled="!isGuideSelectable(guide)">
                  </td>
                  <td>
                    <span class="text-dark fw-bold d-block fs-6">{{ guide.guide_number }}</span>
                  </td>
                  <td>
                    <span class="text-dark fw-bold d-block fs-6">
                      {{ guide.patient?.patient_name || guide.patient_name || '-' }}
                    </span>
                  </td>
                  <td>
                    <span v-if="guide.guide_type" class="badge badge-info">{{ guide.guide_type }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td v-if="!filters.batch_type_available">
                    <span :class="statusClass(guide.status)">
                      {{ statusLabel(guide) }}
                    </span>
                  </td>
                  <td v-if="filters.batch_type_available">
                    <span class="text-muted fw-semibold d-block fs-7">{{ guide.professional_name || '-' }}</span>
                  </td>
                  <td v-if="filters.batch_type_available">
                    <span class="text-muted fw-semibold d-block fs-7">{{ guide.insurance_name || '-' }}</span>
                  </td>
                  <td v-if="filters.batch_type_available">
                    <span class="fw-semibold d-block fs-7">R$ {{ formatCurrency(guideAmount(guide)) }}</span>
                  </td>
                  <td>
                    <span class="text-muted fw-semibold d-block fs-7">
                      {{ formatDate(guide.created_at) }}
                    </span>
                  </td>
                  <td class="text-end">
                    <button
                      v-if="guide.id_card_trans || guide.guide_charge_payment_method || guide.cardTransaction"
                      class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-2"
                      @click="openChargeActions(guide)"
                      title="Cobrança (Pix/Boleto)"
                    >
                      <i class="bi bi-receipt"></i>
                    </button>
                    <button class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-2" 
                            @click="exportPdf(guide.id_tiss_guide)" 
                            title="Abrir PDF em nova aba">
                      <i class="bi bi-file-earmark-pdf"></i>
                    </button>
                    <button class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-2" 
                            @click="exportXml(guideId(guide))" 
                            title="Exportar XML">
                      <i class="bi bi-file-earmark-code"></i>
                    </button>
                    <button class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-2" 
                            @click="openModal(guide)" 
                            title="Editar">
                      <i class="bi bi-pencil-square"></i>
                    </button>
                    <button v-if="!isGuideInBatch(guide)"
                            class="btn btn-icon btn-bg-light btn-active-color-danger btn-sm" 
                            @click="confirmDelete(guideId(guide))"
                            title="Excluir">
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="!guides.data || guides.data.length === 0">
                  <td :colspan="tableColspan" class="text-center text-muted py-10">
                    <i class="bi bi-inbox fs-1"></i>
                    <div class="mt-3">Nenhuma guia encontrada</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <Pagination :data="guides" v-if="guides.data && guides.data.length > 0" />
        </div>
      </div>
    </div>

    <TissGuideModal :show="showModal" :guide="selectedGuide" @close="closeModal" @saved="fetchGuides"/>
    
    <!-- Modal Configurações -->
    <!-- <ModalTissGuideSettings ref="modalSettings"/> -->

    <!-- Modal Fechar Lote -->
    <TissBatchModal 
      :show="showBatchModal" 
      :selectedGuides="selectedGuides"
      :initial-batch-type="filters.batch_type_available"
      @close="closeBatchModal" 
      @saved="onBatchCreated"/>

    <TissGuideChargeActionsModal
      ref="chargeActionsModal"
      :guideId="chargeActionsGuideId"
      :chargeData="chargeActionsChargeData"
      @viewPix="openPixFromChargeActions"
    />

    <PixPaymentModal
      ref="pixPaymentModal"
      :pixData="pixModalData"
      @onClose="pixModalData = null"
    />
  </div>
</template>

<script>
import http from "@/helpers/http";
import Toolbar from "@/components/layouts/auth/Toolbar.vue";
import Pagination from "@/components/Pagination.vue";
import TissGuideModal from "./TissGuideModal.vue";
//import ModalTissGuideSettings from "./ModalTissGuideSettings.vue";
import TissBatchModal from "./TissBatchModal.vue";
import Alert from "@/helpers/alert";
import Download from "@/helpers/download";
import Loader from "@/helpers/loader";
import Permission from "@/helpers/permission";
import TissGuideChargeActionsModal from "@/components/TissGuideChargeActionsModal.vue";
import PixPaymentModal from "@/components/PixPaymentModal.vue";
import { toPixQrCodeDataUrl } from "@/helpers/pixQrCode";

export default {
  name: "TissGuides",
  components: {
    Toolbar,
    Pagination,
    TissGuideModal,
    //ModalTissGuideSettings,
    TissBatchModal,
    TissGuideChargeActionsModal,
    PixPaymentModal,
  },
  data() {
    return {
      guides: {
        data: [],
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1,
      },
      filters: {
        search: '',
        guide_type: '',
        status: '',
        start_date: '',
        end_date: '',
        id_prof: '',
        id_insurance: '',
        batch_type_available: '',
      },
      showModal: false,
      selectedGuide: null,
      showBatchModal: false,
      selectedGuides: [],
      professionals: [],
      insurances: [],
      chargeActionsGuideId: null,
      chargeActionsChargeData: null,
      pixModalData: null,
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    isSelectableBatchMode() {
      return this.filters.batch_type_available === 'tiss' || this.filters.batch_type_available === 'payment';
    },
    tableColspan() {
      const base = 6;
      if (this.filters.batch_type_available) return base + 3;
      return base + 1;
    },
    allSelected() {
      if (!this.guides.data || this.guides.data.length === 0) return false;
      const selectable = this.guides.data.filter(g => this.isGuideSelectable(g));
      return selectable.length > 0 && selectable.every(g => this.selectedGuides.includes(this.guideId(g)));
    },
  },
  async mounted() {
    this.fetchGuides();
    if (this.place?.id_place) {
      try {
        await this.$store.dispatch('auth/loadProfessionals');
      } catch (e) {
        console.error('Erro ao carregar profissionais:', e);
      }
    }
    this.loadProfessionals();
    this.loadInsurances();
  },
  methods: {
    async openChargeActions(guide) {
      if (!guide?.id_tiss_guide) {
        return;
      }

      try {
        Loader.show('Carregando cobrança...');
        const response = await http.get(
          `/places/${this.place.id_place}/tiss-guides/${guide.id_tiss_guide}/charge`
        );
        this.chargeActionsGuideId = guide.id_tiss_guide;
        this.chargeActionsChargeData = response.data.charge;
        this.$nextTick(() => {
          this.$refs.chargeActionsModal?.open();
        });
      } catch (e) {
        Alert.error(e.response?.data?.message || 'Erro ao carregar cobrança da guia');
      } finally {
        Loader.close();
      }
    },
    openPixFromChargeActions() {
      if (!this.chargeActionsChargeData?.pix_code) {
        return;
      }

      this.pixModalData = {
        pix_code: this.chargeActionsChargeData.pix_code,
        qr_code_base64: toPixQrCodeDataUrl(this.chargeActionsChargeData.pix_qrcode_img),
        payment_id: this.chargeActionsChargeData.id_card_trans,
        expires_at: this.chargeActionsChargeData.expires_at,
      };

      this.$nextTick(() => {
        this.$refs.pixPaymentModal?.open();
      });
    },
    fetchGuides() {
      if (this.filters.batch_type_available === 'tiss' || this.filters.batch_type_available === 'payment') {
        this.fetchAvailableForBatch();
        return;
      }

      const params = {
        page: this.guides.current_page,
      };
      if (this.filters.search) params.search = this.filters.search;
      if (this.filters.guide_type) params.guide_type = this.filters.guide_type;
      if (this.filters.status) params.status = this.filters.status;
      if (this.filters.start_date) params.start_date = this.filters.start_date;
      if (this.filters.end_date) params.end_date = this.filters.end_date;
      if (this.filters.id_prof) params.id_prof = this.filters.id_prof;
      if (this.filters.id_insurance) params.id_insurance = this.filters.id_insurance;

      http.get(`/places/${this.place.id_place}/tiss-guides`, { params })
        .then((response) => {
          this.guides = response.data;
        })
        .catch((error) => {
          Alert.error('Erro ao carregar guias');
          console.error(error);
        });
    },
    onBatchAvailabilityFilterChange() {
      this.selectedGuides = [];
      this.guides.current_page = 1;
      this.fetchGuides();
    },
    fetchAvailableForBatch() {
      const params = { batch_type: this.filters.batch_type_available };
      if (this.filters.start_date) params.start_date = this.filters.start_date;
      if (this.filters.end_date) params.end_date = this.filters.end_date;
      if (this.filters.id_prof) params.id_prof = this.filters.id_prof;
      if (this.filters.id_insurance) params.id_insurance = this.filters.id_insurance;

      http.get(`/places/${this.place.id_place}/tiss-guides/available-for-batch`, { params })
        .then((response) => {
          const list = response.data?.data ?? response.data ?? [];
          const rows = Array.isArray(list) ? list : [];
          this.guides = {
            data: rows,
            current_page: 1,
            last_page: 1,
            per_page: rows.length || 15,
            total: rows.length,
            from: rows.length ? 1 : 0,
            to: rows.length,
          };
          // Remove seleção de guias que não estão mais na lista filtrada
          const visibleIds = new Set(rows.map(g => this.guideId(g)));
          this.selectedGuides = this.selectedGuides.filter(id => visibleIds.has(id));
        })
        .catch((error) => {
          this.guides = {
            data: [],
            current_page: 1,
            last_page: 1,
            per_page: 15,
            total: 0,
            from: 0,
            to: 0,
          };
          this.selectedGuides = [];
          const data = error.response?.data;
          if (error.response?.status === 422 && data?.error) {
            Alert.error(data.error);
            return;
          }
          if (error.response?.status === 403 && data?.error) {
            Alert.error(data.error);
            return;
          }
          Alert.error('Erro ao carregar guias disponíveis para lote');
          console.error(error);
        });
    },
    openModal(guide = null) {
      if (guide && guide.id_tiss_guide) {
        http.get(`/places/${this.place.id_place}/tiss-guides/${guide.id_tiss_guide}`)
          .then((response) => {
            this.selectedGuide = response.data;
            // Aguardar o próximo ciclo para garantir que o guide foi atualizado
            this.$nextTick(() => {
              this.showModal = true;
            });
          })
          .catch((error) => {
            Alert.error('Erro ao carregar dados da guia');
            console.error(error);
          });
      } else {
        // Nova guia
        this.selectedGuide = null;
        this.showModal = true;
      }
    },
    closeModal() {
      this.showModal = false;
      this.selectedGuide = null;
    },
    /*openSettings() {
      this.$refs.modalSettings.open();
    },*/
    exportPdf(guideId) {
      Loader.show('Abrindo PDF...');

      http.get(`/places/${this.place.id_place}/tiss-guides/${guideId}/export-pdf`, {
        responseType: 'blob'
      })
      .then((response) => {
        Download.openPdf(response.data);
        Loader.close();
      })
      .catch((error) => {
        console.error('Erro ao exportar PDF:', error);
        Loader.close();
        Alert.error('Erro ao exportar PDF');
      });
    },
    exportXml(guideId) {
      Loader.show('Baixando, por favor aguarde...');

      http.get(`/places/${this.place.id_place}/tiss-guides/${guideId}/export-xml`, {
        responseType: 'blob'
      })
      .then((response) => {
        Download.downloadFile(response.data, `guia_tiss_${guideId}.xml`, 'application/xml');
        Loader.close();
      })
      .catch((error) => {
        console.error('Erro ao exportar XML:', error);
        Loader.close();
        Alert.error('Erro ao exportar XML');
      });
    },
    confirmDelete(guideId) {
      Swal.fire({
        title: "Tem certeza?",
        text: "Essa ação não pode ser desfeita!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, excluir!",
        cancelButtonText: "Cancelar"
      }).then(result => {
        if (result.isConfirmed) {
          http.delete(`/places/${this.place.id_place}/tiss-guides/${guideId}`)
            .then(() => {
              this.fetchGuides();
              Swal.fire("Excluído!", "A guia foi excluída com sucesso.", "success");
            })
            .catch((error) => {
              Alert.error(error.response?.data?.message || error.response?.data?.error || 'Erro ao excluir guia');
              console.error(error);
            });
        }
      });
    },
    statusClass(status) {
      const classes = {
        generated: 'badge badge-primary',
        sent: 'badge badge-info',
        processed: 'badge badge-success',
        rejected: 'badge badge-warning',
        cancelled: 'badge badge-danger',
      };
      return classes[status] || 'badge badge-secondary';
    },
    wasReceivedAtCreation(guide) {
      const bill = guide?.billToReceive || guide?.bill_to_receive;
      if (bill && (bill.bill_to_receive_status === 'received' || bill.bill_to_receive_received_at)) {
        return true;
      }
      const transaction = guide?.cardTransaction || guide?.card_transaction;
      return transaction?.status === 'S';
    },
    isActiveBatchLink(batch) {
      if (!batch) return false;
      if (batch.deleted_at) return false;
      return ['draft', 'closed', 'sent', 'processed'].includes(batch.status);
    },
    availableForReceipt(guide) {
      if (typeof guide?.available_for_receipt === 'boolean') {
        return guide.available_for_receipt;
      }
      const batch = guide?.batchReceipt || guide?.batch_receipt;
      return !this.isActiveBatchLink(batch) && !this.wasReceivedAtCreation(guide);
    },
    availableForPayment(guide) {
      if (typeof guide?.available_for_payment === 'boolean') {
        return guide.available_for_payment;
      }
      const batch = guide?.batchPayment || guide?.batch_payment;
      return !this.isActiveBatchLink(batch);
    },
    statusLabel(guide) {
      if (guide && typeof guide === 'object' && guide.status_label) {
        return guide.status_label;
      }
      const status = typeof guide === 'string' ? guide : guide?.status;
      if (status === 'generated' && guide && typeof guide === 'object') {
        const inReceipt = this.isActiveBatchLink(guide?.batchReceipt || guide?.batch_receipt);
        const inPayment = this.isActiveBatchLink(guide?.batchPayment || guide?.batch_payment);
        if (inReceipt && inPayment) return 'Gerada para recebimento e pagamento';
        if (inReceipt) return 'Gerada para recebimento';
        if (inPayment) return 'Gerada para pagamento';
        return 'Gerada';
      }
      const labels = {
        generated: 'Gerada',
        sent: 'Enviada',
        processed: 'Processada',
        rejected: 'Glosada',
        cancelled: 'Cancelada',
      };
      return labels[status] || status;
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('pt-BR');
    },
    formatCurrency(value) {
      return parseFloat(value || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    hasPermission(permission) {
      return Permission.has(permission);
    },
    guideId(guide) {
      return guide.id_tiss_guide ?? guide.id;
    },
    isGuideInBatch(guide) {
      return this.isActiveBatchLink(guide?.batchReceipt || guide?.batch_receipt)
        || this.isActiveBatchLink(guide?.batchPayment || guide?.batch_payment);
    },
    guideAmount(guide) {
      if (guide?.amount != null && guide.amount !== '') {
        return parseFloat(guide.amount) || 0;
      }
      if (this.filters.batch_type_available === 'payment') {
        return parseFloat(guide?.procedure_transfer_value ?? 0) || 0;
      }
      return parseFloat(guide?.procedure_value ?? 0) || 0;
    },
    isGuideSelectable(guide) {
      if (this.filters.batch_type_available === 'payment') {
        return this.availableForPayment(guide);
      }
      if (this.filters.batch_type_available === 'tiss') {
        return this.availableForReceipt(guide);
      }
      // Modo "Todos": a API valida o tipo ao criar o lote (RF02 permite 1 de cada tipo)
      return guide.status === 'generated';
    },
    toggleSelectAll() {
      const selectable = this.guides.data.filter(g => this.isGuideSelectable(g));
      const ids = selectable.map(g => this.guideId(g));
      if (this.allSelected) {
        this.selectedGuides = this.selectedGuides.filter(id => !ids.includes(id));
      } else {
        this.selectedGuides = [...new Set([...this.selectedGuides, ...ids])];
      }
    },
    openBatchModal() {
      if (!this.isSelectableBatchMode) {
        Alert.error('Selecione o filtro Disponíveis para lote (recebimento ou pagamento) antes de criar o lote');
        return;
      }
      if (this.selectedGuides.length === 0) {
        Alert.error('Selecione pelo menos uma guia para criar o lote');
        return;
      }
      this.showBatchModal = true;
    },
    closeBatchModal() {
      this.showBatchModal = false;
    },
    onBatchCreated() {
      this.selectedGuides = [];
      this.fetchGuides();
    },
    loadProfessionals() {
      this.professionals = this.$store.state.auth.professionals || [];
    },
    loadInsurances() {
      if (!this.place?.id_place) return;
      http.get(`/places/${this.place.id_place}/insurances`)
        .then((response) => {
          this.insurances = response.data || [];
        })
        .catch((error) => {
          console.error('Erro ao carregar convênios:', error);
        });
    },
  },
};
</script>

<style scoped>
</style>

